import React, {
  ComponentClass,
  createElement,
  Fragment,
  FunctionComponent,
  isValidElement,
  memo,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import { AccessDeniedStrategy, AuthorizationChecker } from "@tecorien/mimic-v2";
import classNames from "classnames";
import { To } from "react-router";
import { Dropdown, DropdownProps } from "../Dropdown";
import { Link } from "../Link";
import { MoreItemsButton } from "./MoreItemsButton";

import styles from "./style.module.scss";

export type AdditionalActionDescription = {
  to?: To,
  onClick?: MouseEventHandler<HTMLButtonElement>,
  key?: string,
  icon?: ReactNode | FunctionComponent<any> | ComponentClass<any>,
  content: ReactNode,
  component?: string | FunctionComponent<any> | ComponentClass<any>,
  additionalContent?: ReactNode,
  disabled?: boolean,
  requiredPermissions?: string | string[]
  requiredOneOfPermissions?: string | string[]
};

export type AdditionalActionsProps = {
  toggleButtonClassName?: string,
  dropdownClassName?: string,
  dropdownItemClassName?: string,
  dropDownItemContentClassName?: string,
  actions?: AdditionalActionDescription[],
  icon?: ReactNode
  dropDownProps?: DropdownProps
};

export const AdditionalActions = memo<AdditionalActionsProps>((
  {
    toggleButtonClassName,
    actions,
    icon,
    dropdownClassName,
    dropdownItemClassName,
    dropDownItemContentClassName,
    dropDownProps,
  }) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const refObject = useRef<HTMLButtonElement>(null);
  const onRequestClose = useCallback(() => setIsOpen(false), [ setIsOpen ]);

  if (!actions?.length) {
    return null;
  }

  return (
    <>
      <MoreItemsButton
        ref={refObject}
        onClick={() => setIsOpen(!isOpen)}
        className={toggleButtonClassName}
        icon={icon}
        data-is-open={isOpen}
      />
      <Dropdown
        popperOptions={{ placement: "bottom-end" }}
        {...dropDownProps}
        isOpen={isOpen}
        className={classNames(styles.dropDown, dropdownClassName)}
        onRequestClose={onRequestClose}
        referenceElement={refObject?.current}
      >
        {
          isOpen && actions?.map((item, index) => {
            return (
              <AuthorizationChecker
                key={item.key || (`${item.content}_${index}`)}
                requiredPermissions={item.requiredPermissions}
                requiredOneOfPermissions={item.requiredOneOfPermissions}
                strategy={AccessDeniedStrategy.hide}
              >
                <Link
                  className={classNames(styles.dropDownItem, dropdownItemClassName)}
                  component={item.component || (!item.to ? "button" : undefined)}
                  type="button"
                  linkType="s"
                  onClick={item.onClick}
                  to={item.to}
                  disabled={item.disabled}
                >
                  <div className={classNames(styles.dropDownItemContent, dropDownItemContentClassName)}>
                    {item?.icon && (isValidElement(item?.icon) ? item?.icon : createElement(item?.icon as any))}
                    {item.content}
                  </div>
                </Link>
              </AuthorizationChecker>
            );
          })
        }
      </Dropdown>
      {
        actions?.filter(x => x.additionalContent)
          .map((item, index) => {
            return (
              <Fragment key={item.key || (`${item.content}_${index}`)}>
                {item.additionalContent}
              </Fragment>
            );
          })
      }
    </>
  );
});

export default AdditionalActions;
